// Lone Rider — Moto Lights configurator popup
//
// The shared modal behind every section CTA. Opened with the light set already
// decided by the CTA that was clicked:
//
//     openConfigurator('mtl4' | 'mtl9' | 'bundle', triggerElement)
//
// or declaratively, which is what the sections should use:
//
//     <button data-open-configurator="mtl9">…</button>
//
// A delegated listener handles the attribute form, so a CTA added in a later section
// works with no JS at the call site and nothing here to update.
//
// REUSES, rather than reimplementing:
//   theme.Modals      open/close, ESC, click-outside, focus trap, body scroll lock
//   .lr-select / .lr-config__checkbox   the same controls as the buybox journey
//   moto-lights-notify + waitlist-form  the Klaviyo notify-me flow
//   ajaxProduct:added                   opens the theme cart drawer
//
// THE SELECTION IS ITS OWN. MotoLightsConfigurator.create() gives the popup a
// separate instance over the shared catalogue, so opening a popup preset to MTL9
// does not rewrite the buybox behind it. That is the theme's own rule for modals.
//
// ONE SCREEN, NOT A WIZARD. Make, then Model, then Mount, each revealed as the one
// above it is answered, with Add to cart underneath — the same journey as the buybox.
// Paging through them with Next/Back put Add to cart on a screen where nothing had
// been chosen yet, and the light set is already decided by the CTA, so there were
// only ever two or three answers to collect.
//
// Mount is not rendered at all for a bike whose kit only comes with the included Bar
// Clamp: there is nothing to tick.
//
// ONE DELIBERATE DEVIATION FROM THE SPEC, and why:
// The brief says step 2 should list "only models where the preset light option
// exists". Taken literally, a rider whose bike has no bundle would not find their
// bike in the list at all and would conclude it is unsupported — and the brief's own
// next bullet, "point the rider to the alternative that IS available", could then
// never fire. So every model for the make is listed, and choosing one the preset
// does not cover shows that message plus a button that switches the popup to the set
// which does cover it. Nothing unavailable can be bought either way.
(() => {
  'use strict';

  const MODAL_ID = 'MotoLightsConfiguratorModal';
  const INCLUDED_MOUNT = 'barclamp';

  const ARROW_SVG =
    '<svg viewBox="0 0 10 6" width="10" height="6" aria-hidden="true">' +
    '<path d="M0 0l5 6 5-6z" fill="currentColor"/></svg>';

  // Filled by boot(); openConfigurator() waits on it.
  let api = null;
  let booting = null;

  const fill = (tpl, map) =>
    String(tpl || '').replace(/\[(\w+)\]/g, (m, k) => (map[k] != null ? map[k] : m));

  // ---------- one .lr-select, same markup and ARIA as the buybox journey ----------
  function buildSelect(stepKey, step, uid, locked) {
    const wrapper = document.createElement('div');
    wrapper.className = 'variant-wrapper variant-wrapper--dropdown js lr-config__step';

    const labelId = 'MLM-' + uid + '-' + stepKey + '-label';
    const listId = 'MLM-' + uid + '-' + stepKey + '-list';

    const label = document.createElement('label');
    label.className = 'variant__label';
    label.id = labelId;
    label.textContent = step.label || stepKey;

    const inputWrap = document.createElement('div');
    inputWrap.className = 'variant-input-wrap';

    const cd = document.createElement('div');
    // .lr-select--locked is the theme's own locked-step styling (custom.css).
    cd.className = 'lr-select' + (locked ? ' lr-select--locked' : '');

    const trigger = document.createElement('div');
    trigger.className = 'lr-select__trigger';
    if (locked) {
      // Nothing to expand and nothing to choose, so it is not a control: left out of
      // the tab order and not announced as a listbox.
      trigger.setAttribute('aria-labelledby', labelId);
    } else {
      trigger.setAttribute('role', 'combobox');
      trigger.setAttribute('tabindex', '0');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-controls', listId);
      trigger.setAttribute('aria-labelledby', labelId);
    }

    const labelEl = document.createElement('span');
    labelEl.className = 'lr-select__label';

    const arrow = document.createElement('span');
    arrow.className = 'lr-select__arrow';
    arrow.innerHTML = ARROW_SVG;

    trigger.appendChild(labelEl);
    if (!locked) trigger.appendChild(arrow);

    const list = document.createElement('ul');
    list.className = 'lr-select__list';
    list.id = listId;
    list.setAttribute('role', 'listbox');
    list.setAttribute('aria-labelledby', labelId);

    cd.appendChild(trigger);
    cd.appendChild(list);
    inputWrap.appendChild(cd);
    wrapper.appendChild(label);
    wrapper.appendChild(inputWrap);

    return { key: stepKey, wrapper, cd, trigger, labelEl, list, signature: null };
  }

  // ---------- the mount ticks ----------
  //
  // Same control as the buybox: Bar Clamp ships with the lights, so these are opt-in
  // upgrades rather than a pick-one list. Built only for bikes that offer one.
  function buildCheckboxStep(stepKey, step, labels, uid) {
    const wrapper = document.createElement('div');
    wrapper.className = 'variant-wrapper js lr-config__step lr-config__step--checkbox is-hidden';

    const label = document.createElement('label');
    label.className = 'variant__label';
    label.id = 'MLM-' + uid + '-' + stepKey + '-label';
    label.textContent = (step && step.label) || stepKey;

    const group = document.createElement('div');
    group.className = 'lr-config__checkbox-group';
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', label.id);

    wrapper.appendChild(label);
    wrapper.appendChild(group);

    return { key: stepKey, wrapper, group, isCheckbox: true, rows: [], signature: null };
  }

  function boot() {
    if (booting) return booting;

    const modalEl = document.getElementById(MODAL_ID);
    if (!modalEl) return Promise.reject(new Error('moto lights modal is not on this page'));

    const q = (sel) => modalEl.querySelector(sel);

    let labels;
    try {
      labels = JSON.parse(q('[data-ml-modal-labels]').textContent);
    } catch (e) {
      return Promise.reject(e);
    }

    const stepsHost = q('[data-ml-modal-steps]');
    const headingEl = q('[data-ml-modal-heading]');
    const mismatch = q('[data-ml-modal-mismatch]');
    const mismatchCopy = q('[data-ml-modal-mismatch-copy]');
    const alternatives = q('[data-ml-modal-alternatives]');
    const summary = q('[data-ml-modal-summary]');
    const summaryHeading = q('[data-ml-modal-summary-heading]');
    const summaryText = q('[data-ml-modal-summary-text]');
    const priceEl = q('[data-ml-modal-price]');
    const media = q('[data-ml-modal-media]');
    const mediaImg = q('[data-ml-modal-image]');
    const mediaPlaceholder = q('[data-ml-modal-media-placeholder]');
    const notice = q('[data-ml-notice]');
    const errorEl = q('[data-ml-modal-error]');
    const atcBtn = q('[data-ml-modal-atc]');
    const atcText = q('[data-ml-modal-atc-text]');
    const atcPrice = q('[data-ml-modal-atc-price]');
    const atcDivider = q('[data-ml-modal-atc-divider]');

    atcText.textContent = labels.addToCart;
    summaryHeading.textContent = labels.summaryHeading;

    // The photo shown for each light set, already sized by the CDN.
    const SET_IMAGES = {
      mtl4: 'https://lonerider.com/cdn/shop/files/MTL4-set.jpg?v=1790863038&width=600',
      mtl9: 'https://lonerider.com/cdn/shop/files/MTL9-set.jpg?v=1790863038&width=600',
      bundle: 'https://lonerider.com/cdn/shop/files/MTL9_4-sets.jpg?v=1790863038&width=600'
    };

    booting = window.MotoLightsConfigurator.create().then((config) => {
      // The popup's own selection — deliberately not the buybox's.
      let presetSet = 'mtl4';
      let lastTrigger = null;

      // --- build the three steps once, in order ---
      const ui = {
        make: buildSelect('make', labels.steps.make, 'modal'),
        model: buildSelect('model', labels.steps.model, 'modal'),
        // Locked: every kit includes the MotoCAN V2 harness, so it is shown to say
        // what is included rather than to ask anything. Same step, same place, as
        // the MTL9 PDP's configurator.
        motocan: buildSelect('motocan', labels.steps.motocan, 'modal', true),
        mount: buildCheckboxStep('mount', labels.steps.mount, labels, 'modal')
      };
      ['make', 'model', 'motocan', 'mount'].forEach((k) => stepsHost.appendChild(ui[k].wrapper));

      // The note under it, explaining why it cannot be changed.
      (function () {
        const note = document.createElement('p');
        note.className = 'lr-ml__note';
        note.textContent = labels.motocanIncluded || '';
        ui.motocan.wrapper.appendChild(note);
      })();

      const closeLists = () => {
        ['make', 'model'].forEach((k) => {
          const b = ui[k];
          b.cd.classList.remove('is-open');
          b.trigger.setAttribute('aria-expanded', 'false');
        });
      };

      // --- theme modal: open/close, ESC, focus trap, body scroll lock ---
      const modal = new theme.Modals(MODAL_ID, 'moto-lights-configurator', {
        closeOffContentClick: true
      });

      // theme.Modals only restores focus when it handled the opening click itself
      // (and its own restore is broken — it calls .focus() on the return of
      // setAttribute). This popup opens programmatically, so that is ours to do.
      // BODY SCROLL LOCK.
      //
      // theme.Modals adds `modal-open` to <html>, but the rule that acts on it —
      // `.modal-open { overflow: hidden }` in theme.css — sits inside a
      // `@media (min-width: 590px)` block, so on a phone the page behind the modal
      // still scrolls. This adds a lock of our own, under a class of our own so no
      // other modal's behaviour changes.
      //
      // The scroll position is captured and restored by hand: `position: fixed` on
      // the body is the only thing iOS reliably respects, and it would otherwise
      // dump the rider back at the top of the page when the popup closes.
      let lockedScrollY = 0;

      function lockScroll() {
        lockedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
        document.documentElement.classList.add('ml-modal-open');
        document.body.style.top = '-' + lockedScrollY + 'px';
      }

      function unlockScroll() {
        document.documentElement.classList.remove('ml-modal-open');
        document.body.style.top = '';
        window.scrollTo(0, lockedScrollY);
      }

      document.addEventListener('modalClose.' + MODAL_ID, () => {
        closeLists();
        unlockScroll();
        if (lastTrigger && typeof lastTrigger.focus === 'function') {
          lastTrigger.focus();
          lastTrigger = null;
        }
      });

      // Forces the CTA's light set back on: setting make or model clears it.
      function applyPreset() { config.set('set', presetSet); }

      // Opens the next step still needing an answer, so the rider is walked down the
      // list. Mount is skipped — it is always pre-answered.
      function openNext(fromKey) {
        const state = config.state();
        if (fromKey === 'make' && !state.model) {
          if (ui.model.list.querySelector('.lr-select__item[data-value]')) {
            openList(ui.model);
            return;
          }
        }
        closeLists();
      }

      const openList = (built) => {
        closeLists();
        built.cd.classList.add('is-open');
        built.trigger.setAttribute('aria-expanded', 'true');
      };

      // --- wiring: the two dropdowns ---
      ['make', 'model'].forEach((key) => {
        const built = ui[key];

        built.trigger.addEventListener('click', (e) => {
          // theme.Modals closes the modal on any click outside .modal__inner, and
          // lr-make-configurator.js closes every open .lr-select on any document
          // click. Neither should fire for our own trigger.
          e.stopPropagation();
          const open = built.cd.classList.contains('is-open');
          closeLists();
          if (!open) openList(built);
        });

        built.list.addEventListener('click', (e) => {
          const item = e.target.closest('.lr-select__item');
          if (!item || item.getAttribute('aria-disabled') === 'true') return;
          e.stopPropagation();
          closeLists();
          config.set(key, item.getAttribute('data-value'));
          applyPreset();
          openNext(key);
          built.trigger.focus();
        });

        built.trigger.addEventListener('keydown', (e) => {
          const items = Array.from(
            built.list.querySelectorAll('.lr-select__item[data-value]:not([aria-disabled="true"])')
          );
          if (!items.length) return;
          const open = built.cd.classList.contains('is-open');
          const cur = items.findIndex((el) => el.classList.contains('is-active'));
          const move = (to) => {
            items.forEach((el) => el.classList.remove('is-active'));
            const el = items[Math.max(0, Math.min(items.length - 1, to))];
            if (!el) return;
            el.classList.add('is-active');
            built.trigger.setAttribute('aria-activedescendant', el.id);
            if (el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
          };

          switch (e.key) {
            case 'Enter':
            case ' ':
              e.preventDefault();
              if (!open) { openList(built); move(cur < 0 ? 0 : cur); return; }
              if (cur > -1) {
                closeLists();
                config.set(key, items[cur].getAttribute('data-value'));
                applyPreset();
                openNext(key);
              }
              return;
            case 'ArrowDown':
              e.preventDefault(); if (!open) openList(built); move(cur < 0 ? 0 : cur + 1); return;
            case 'ArrowUp':
              e.preventDefault(); if (!open) openList(built); move(cur < 0 ? items.length - 1 : cur - 1); return;
            case 'Escape':
              // Close the list, not the modal.
              if (!open) return;
              e.preventDefault(); e.stopPropagation(); closeLists(); return;
            default:
              return;
          }
        });
      });

      // --- add to cart ---
      atcBtn.addEventListener('click', () => {
        errorEl.hidden = true;
        atcBtn.classList.add('btn--loading');
        atcBtn.disabled = true;
        config.addToCart(1)
          .then(() => {
            // The cart drawer opening is what confirms it; the popup is done.
            modal.close();
          })
          .catch((err) => {
            errorEl.textContent = (err && err.message) || 'Could not add to cart. Please try again.';
            errorEl.hidden = false;
          })
          .then(() => {
            atcBtn.classList.remove('btn--loading');
            atcBtn.disabled = config.resolve().status !== 'buyable';
          });
      });

      // ---------- render ----------
      function render(snap) {
        const state = snap.state;
        const setLabel = labels.sets[presetSet] || presetSet;

        headingEl.textContent = fill(labels.heading, { set: setLabel });

        // --- Make (always shown) ---
        ui.make.wrapper.classList.remove('is-hidden');
        renderList(ui.make, snap.options.makes.map((o) => ({ value: o.value, label: o.label })),
          labels.steps.make.placeholder, state.make);

        // --- Model (once a make is chosen) ---
        ui.model.wrapper.classList.toggle('is-hidden', !state.make);
        renderList(ui.model, snap.options.models.map((o) => ({ value: o.value, label: o.label })),
          labels.steps.model.placeholder, state.model);

        // --- does the CTA's set exist for this bike? ---
        const setRow = snap.options.sets.filter((s) => s.value === presetSet)[0];
        const presetMissing = !!state.model && (!setRow || !setRow.exists);
        mismatch.hidden = !presetMissing;
        if (presetMissing) {
          mismatchCopy.textContent = fill(labels.notAvailable, { set: setLabel });
          renderAlternatives(snap);
        }

        // --- MotoCAN (locked, informational) ---
        ui.motocan.wrapper.classList.toggle('is-hidden', !state.model || presetMissing);
        ui.motocan.labelEl.textContent = config.motocanLabel();

        // --- Mount: only when the bike actually offers a choice ---
        const upgrades = snap.options.mounts.filter((m) => m.value !== INCLUDED_MOUNT);
        const showMount = !!state.model && !presetMissing && upgrades.length > 0;
        ui.mount.wrapper.classList.toggle('is-hidden', !showMount);
        if (showMount) renderMounts(ui.mount, snap, upgrades);

        // --- summary, image and price ---
        const hasEntry = !!snap.entry;
        summary.hidden = !hasEntry;
        if (hasEntry) {
          summaryText.textContent = snap.summary;
          priceEl.textContent = snap.priceFormatted;
        }
        // No price for a bike that can only be signed up for (coming soon, sold out).
        priceEl.hidden = snap.status === 'unavailable';
        renderMedia(snap, hasEntry);

        // --- buy, or sign up. Never both. ---
        const buyable = snap.status === 'buyable';
        const unavailable = snap.status === 'unavailable' && !presetMissing;

        atcBtn.hidden = unavailable;
        atcBtn.disabled = !buyable;
        atcPrice.textContent = buyable ? snap.priceFormatted : '';
        atcDivider.hidden = !buyable;

        if (notice) notice.hidden = !unavailable;
        if (unavailable) {
          const soon = modalEl.querySelector('[data-ml-notice-soon]');
          const oos = modalEl.querySelector('[data-ml-notice-oos]');
          const isSoon = snap.reason === 'coming_soon' || snap.reason === 'no_variant';
          if (soon) soon.hidden = !isSoon;
          if (oos) oos.hidden = isSoon;
          config.syncNotify(notice);
        }

        errorEl.hidden = true;
      }

      // The variant's own image. Shopify serves these full size, so ask the CDN for
      // a sensible width rather than pulling a 2000px file into the modal.
      function renderMedia(snap, hasEntry) {
        if (!media || !mediaImg) return;
        // Each light set has a dedicated set photo, shown whatever bike is chosen.
        // Anything else falls back to the variant's image, then the set product's
        // featured image, so the popup never shows an empty frame.
        const setPhoto = SET_IMAGES[presetSet];
        const src = setPhoto || (hasEntry && snap.image) || config.setImage(presetSet) || '';
        if (src) {
          const sized = (!setPhoto && (src.indexOf('cdn.shopify.com') > -1 || src.indexOf('/cdn/') > -1))
            ? src.replace(/(\.(?:jpe?g|png|webp|gif))(\?|$)/i, '_900x$1$2')
            : src;
          if (mediaImg.getAttribute('src') !== sized) mediaImg.setAttribute('src', sized);
          mediaImg.setAttribute('alt', snap.summary || '');
          mediaImg.hidden = false;
          if (mediaPlaceholder) mediaPlaceholder.hidden = true;
        } else {
          mediaImg.hidden = true;
          mediaImg.removeAttribute('src');
          if (mediaPlaceholder) mediaPlaceholder.hidden = false;
        }
      }

      // The sets that DO exist for the chosen bike, each offered as a switch.
      function renderAlternatives(snap) {
        alternatives.innerHTML = '';
        const others = snap.options.sets.filter((s) => s.exists && s.value !== presetSet);
        if (!others.length) return;

        const lead = document.createElement('p');
        lead.className = 'lr-ml__note';
        lead.textContent = labels.alternativeLead;
        alternatives.appendChild(lead);

        others.forEach((s) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'btn btn--secondary';
          btn.textContent = fill(labels.switchTo, { set: labels.sets[s.value] || s.value });
          btn.addEventListener('click', () => {
            presetSet = s.value;   // keep the bike, change the kit
            applyPreset();
          });
          alternatives.appendChild(btn);
        });
      }

      function renderList(built, rows, placeholder, value) {
        const signature = JSON.stringify(rows.map((r) => [r.value, r.label])) + '|' + value;
        if (built.signature === signature) return;
        built.signature = signature;

        built.list.innerHTML = '';
        let selectedLabel = '';
        rows.forEach((row, i) => {
          const li = document.createElement('li');
          li.className = 'lr-select__item' + (row.value === value ? ' is-selected' : '');
          li.id = built.list.id + '-opt-' + i;
          li.setAttribute('role', 'option');
          li.setAttribute('data-value', row.value);
          li.setAttribute('aria-selected', row.value === value ? 'true' : 'false');
          li.textContent = row.label;
          built.list.appendChild(li);
          if (row.value === value) selectedLabel = row.label;
        });
        built.labelEl.textContent = selectedLabel || placeholder || '';
        built.cd.classList.toggle('is-empty', !selectedLabel);
      }

      // Same rule as the buybox: Bar Clamp is included, the rest are opt-in ticks,
      // and unticking falls back to Bar Clamp.
      function renderMounts(built, snap, upgrades) {
        const signature = JSON.stringify(upgrades.map((u) => [u.value, u.label]));
        const hasIncluded = snap.options.mounts.some((m) => m.value === INCLUDED_MOUNT);

        if (built.signature !== signature) {
          built.signature = signature;
          built.group.innerHTML = '';
          built.rows = upgrades.map((choice) => {
            const label = document.createElement('label');
            label.className = 'lr-config__checkbox';
            const input = document.createElement('input');
            input.type = 'checkbox';
            input.className = 'lr-config__checkbox-input';
            const text = document.createElement('span');
            text.className = 'lr-config__checkbox-text';
            text.textContent = choice.label;
            label.appendChild(input);
            label.appendChild(text);
            built.group.appendChild(label);

            input.addEventListener('change', () => {
              if (input.checked) { config.set('mount', choice.value); return; }
              if (!hasIncluded) { input.checked = true; return; }
              config.set('mount', INCLUDED_MOUNT);
            });
            return { key: choice.value, input: input };
          });
        }

        built.rows.forEach((r) => { r.input.checked = snap.state.mount === r.key; });
      }

      config.subscribe(render);

      // ---------- the public entry point ----------
      function open(set, trigger) {
        if (!labels.sets[set]) {
          console.error('[LR moto lights] openConfigurator: unknown light set', set);
          return;
        }
        presetSet = set;
        lastTrigger = trigger || null;

        // Each opening starts clean, so a popup never inherits the last CTA's bike.
        config.set('make', '');
        applyPreset();

        render(config.snapshot());
        lockScroll();
        modal.open();
      }

      return { open: open, modal: modal };
    });

    return booting;
  }

  // openConfigurator() may be called before the catalogue has loaded, so it always
  // goes through the boot promise rather than assuming readiness.
  function openConfigurator(set, trigger) {
    boot()
      .then((built) => { api = built; built.open(set, trigger); })
      .catch((e) => console.error('[LR moto lights] could not open the configurator', e));
  }

  // Declarative CTAs. Delegated, so a button rendered by any later section works
  // without registering anything here.
  document.addEventListener('click', (e) => {
    const trigger = e.target.closest && e.target.closest('[data-open-configurator]');
    if (!trigger) return;
    e.preventDefault();
    openConfigurator(trigger.getAttribute('data-open-configurator'), trigger);
  });

  window.openConfigurator = openConfigurator;
  window.LRMotoLightsModal = { open: openConfigurator, boot: boot };
})();
